/**
 * Site quick actions — one-tap device commands for the site dashboard.
 *
 * Surfaces the high-value commands an operator reaches for most — request
 * status, request live power, and reboot — as direct buttons instead of making
 * them open the full command panel and build a queue. Buttons are shown when
 * the caller passes `canSendCommand=true` (from the capabilities slice); the
 * command catalog is loaded lazily on first click rather than on mount, so
 * first paint incurs no extra API call. Status/live-power dispatch immediately;
 * the device reset ("Reboot") is confirmed first. Which status frame a unit
 * answers with is the backend's call, handed over with the catalog as
 * `default_status`; a frame a firmware row declares outright sends without
 * further ado, while staff — whose catalog exposes every status frame
 * regardless of unit or customer type — see a chooser dialog for the rest,
 * since no signal separates those frames for them.
 *
 * Contains:
 * - SiteQuickActions — button row + reboot confirm and status-type chooser
 *   dialogs (default export)
 */
import BoltIcon from '@mui/icons-material/Bolt';
import CellTowerIcon from '@mui/icons-material/CellTower';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { selectCapabilities } from '@sophocles/features/capabilities';
import { loadUnitCommands } from '@sophocles/features/commands/api';
import { selectAvailableCommands } from '@sophocles/features/commands/slice';
import { selectSiteUnit } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';
import {
  QUICK_ACTION_KEY_SETS,
  resolveLivePowerKey,
  resolveQuickActionKey,
  resolveStatusDispatch,
  resolveStatusKey,
} from '@sophocles/views/sites/components/SiteDashboard/quickActionResolver';

// Each quick action resolves to the best catalog key for the unit's protocol
// support, so a device that only offers e.g. STATUS_GET_SHORT still gets a
// "Request status" button.
//
// Status resolution precedence (see resolveStatusDispatch): a firmware-
// declared default status command wins outright, for staff and non-staff
// alike. Absent one, unit.firmware_version.supports_v2_protocol prefers
// STATUS_GET_V2 when the catalog has it; the choice among ZONEINFO / SHORT /
// LONG depends on customer type, which the catalog already reflects for
// non-staff (pre-filtered to the correct frame). Staff catalogs expose every
// frame, so staff with more than one candidate pick manually via the
// status-type chooser (see run()); staff with at most one candidate resolve
// the same as everyone else.
//
// Live power prefers STATUS_GET_LIVE_POWER; falls back to the same
// unit-aware status frame. The button renders for ALL units — on a v2 unit
// without STATUS_GET_LIVE_POWER it falls back to STATUS_GET_V2.
const QUICK_SPECS = [
  {
    id: 'status',
    keys: QUICK_ACTION_KEY_SETS.status,
    labelKey: 'sites.requestStatus',
    fallback: 'Request status',
    Icon: CellTowerIcon,
  },
  {
    id: 'livePower',
    keys: QUICK_ACTION_KEY_SETS.livePower,
    labelKey: 'sites.requestLivePower',
    fallback: 'Request live power',
    Icon: BoltIcon,
  },
  {
    id: 'reboot',
    keys: QUICK_ACTION_KEY_SETS.reboot,
    labelKey: 'sites.reboot',
    fallback: 'Reboot',
    Icon: RestartAltIcon,
    confirm: true,
  },
];

export const COMMAND_BUTTON_SX = {
  textTransform: 'none',
  width: '9.5rem',
  maxWidth: '100%',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
};

/**
 * @param {object}        props
 * @param {number|string} props.siteId            - site id (for the catalog + dispatch endpoints)
 * @param {boolean}       [props.canSendCommand]  - capabilities gate; buttons only render when true
 * @param {boolean}       [props.compact]          - icon-only buttons (mobile header) vs labeled buttons (rail)
 * @param {React.node}    [props.appendActions]   - optional command-styled actions appended after quick actions
 */
export default function SiteQuickActions({ siteId, canSendCommand = false, compact = false, appendActions = null }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dispatchCommands, activityLocked } = useCommandDispatch(siteId);

  // Catalog is fetched lazily (on first button click) rather than on mount.
  // The thunk's condition guard ensures only one in-flight request per site.
  const catalogEntry = useSelector(state => selectAvailableCommands(state, siteId));
  const catalog = catalogEntry?.data ?? null;

  // Unit snapshot — needed to read supports_v2_protocol for unit-aware resolution.
  const unit = useSelector(selectSiteUnit(siteId));
  const supportsV2 = unit?.firmware_version?.supports_v2_protocol ?? null;

  // The status frame the backend resolved for this unit — {key, source} —
  // travelling with the catalog it has to be dispatchable against.
  const defaultStatus = catalog?.default_status ?? null;

  const capabilities = useSelector(selectCapabilities);
  const isStaff = capabilities?.is_staff ?? false;

  // busyId tracks the spec ID (not the resolved command key) so two specs that
  // happen to resolve to the same catalog key don't both spin/disable.
  const [busyId, setBusyId] = useState(null);
  const [rebootCommand, setRebootCommand] = useState(null);
  // Catalog command objects for the manual status-type chooser. Non-null only
  // for staff when the catalog exposes more than one status frame — staff
  // catalogs carry every frame, so auto-resolution cannot know the right one.
  const [statusChoices, setStatusChoices] = useState(null);

  // Catalog loads lazily on first click (see run()); not on mount.

  const commandsByKey = useMemo(() => {
    const map = {};
    (catalog?.sections ?? []).forEach(section =>
      (section.commands ?? []).forEach(command => {
        map[command.key] = command;
      }),
    );
    return map;
  }, [catalog]);

  // When the catalog is loaded, only surface actions whose keys are actually
  // available (device/role filtered).  Before the catalog arrives, surface all
  // QUICK_SPECS as stubs so first paint renders buttons without a fetch.
  // Resolution is unit-aware: status and livePower use the v2-protocol flag
  // from the snapshot unit; reboot uses plain first-available resolution.
  const actions = useMemo(() => {
    if (catalog == null) {
      // Pre-catalog stubs: use the first spec key as a best-guess placeholder.
      return QUICK_SPECS.map(spec => ({ ...spec, command: { key: spec.keys[0] } }));
    }
    return QUICK_SPECS.map(spec => {
      let key;
      if (spec.id === 'status') {
        key = resolveStatusKey(commandsByKey, supportsV2, defaultStatus?.key ?? null);
      } else if (spec.id === 'livePower') {
        key = resolveLivePowerKey(commandsByKey, supportsV2, defaultStatus?.key ?? null);
      } else {
        key = resolveQuickActionKey(commandsByKey, spec.keys);
      }
      return key ? { ...spec, command: commandsByKey[key] } : null;
    }).filter(Boolean);
  }, [catalog, commandsByKey, supportsV2, defaultStatus]);

  // Hide quick actions during a firmware upgrade (catalog signals this once loaded).
  // canSendCommand is the capability gate: `actions` is never empty (pre-catalog
  // it is the full QUICK_SPECS stub list), so this MUST be AND — an OR here lets
  // the buttons render for view-only users once the catalog populates.
  const showQuickActions = !catalog?.current_upgrade && canSendCommand && actions.length > 0;

  if (!showQuickActions && !appendActions) return null;

  const notifyDispatchFailure = (failures, messages) => {
    const first = failures[0];
    const message =
      messages[0] ??
      (first ? (first.message_key ? t(first.message_key) : first.detail) : null) ??
      t('commandCatalog.sendError');
    dispatch(addToast({ message, level: 'error' }));
  };

  const run = async (spec, params = {}) => {
    if (activityLocked) return;

    // Key busy state by the spec ID (not the resolved command key) so two specs
    // that share a fallback key don't both spin simultaneously.
    setBusyId(spec.id);
    try {
      // Load catalog lazily on first click so mount incurs no extra request.
      // The thunk's condition guard prevents duplicate in-flight calls.
      let resolvedCatalog = catalog;
      if (!resolvedCatalog) {
        const resultAction = await dispatch(loadUnitCommands(siteId));
        resolvedCatalog = resultAction?.payload ?? null;
      }

      // Build the catalog map and resolve the best available key unit-aware.
      const catalogMap = {};
      (resolvedCatalog?.sections ?? []).forEach(section =>
        (section.commands ?? []).forEach(cmd => {
          catalogMap[cmd.key] = cmd;
        }),
      );

      // Read the resolution off the catalog this dispatch is resolving
      // against, which on a lazy first click is the one just fetched.
      const resolvedStatus = resolvedCatalog?.default_status ?? null;

      let key;
      if (spec.id === 'status') {
        // A declared frame sends immediately; otherwise staff choose manually
        // whenever their (unfiltered) catalog offers more than one frame —
        // see resolveStatusDispatch.
        const dispatchDecision = resolveStatusDispatch({
          resolved: resolvedStatus,
          isStaff,
          commandsByKey: catalogMap,
          supportsV2,
        });
        if (dispatchDecision.type === 'choose') {
          setStatusChoices(dispatchDecision.frames.map(frameKey => catalogMap[frameKey]));
          return;
        }
        key = dispatchDecision.type === 'send' ? dispatchDecision.key : null;
      } else if (spec.id === 'livePower') {
        key = resolveLivePowerKey(catalogMap, supportsV2, resolvedStatus?.key ?? null);
      } else {
        key = resolveQuickActionKey(catalogMap, spec.keys);
      }

      if (!key) {
        dispatch(
          addToast({
            message: t('commandCatalog.sendError', 'Command not available for this device'),
            level: 'warning',
          }),
        );
        return;
      }

      const command = catalogMap[key];
      const { status, failures, messages } = await dispatchCommands([{ key: command.key, params }]);
      // Success/progress feedback arrives from the backend over socket.io, so we
      // only surface a toast when the dispatch itself is rejected (never queued).
      if (status !== DISPATCH_STATUS.SUCCESS) {
        notifyDispatchFailure(failures, messages);
      }
    } catch {
      dispatch(addToast({ message: t('commandCatalog.sendError'), level: 'error' }));
    } finally {
      setBusyId(null);
    }
  };

  const sendStatusFrame = async command => {
    setStatusChoices(null);
    if (activityLocked) return;
    setBusyId('status');
    try {
      const { status, failures, messages } = await dispatchCommands([{ key: command.key, params: {} }]);
      if (status !== DISPATCH_STATUS.SUCCESS) {
        notifyDispatchFailure(failures, messages);
      }
    } catch {
      dispatch(addToast({ message: t('commandCatalog.sendError'), level: 'error' }));
    } finally {
      setBusyId(null);
    }
  };

  const handleClick = (event, action) => {
    event?.stopPropagation?.();
    if (activityLocked || busyId != null) return;
    if (action.confirm) {
      // For confirm-first actions store the spec (not a pre-catalog command object)
      // so confirmReboot can call run() with the spec's key list.
      setRebootCommand(action);
    } else {
      run(action);
    }
  };

  const confirmReboot = () => {
    const spec = rebootCommand;
    setRebootCommand(null);
    if (spec) run(spec);
  };

  const buttons = showQuickActions
    ? actions.map(action => {
        const label = t(action.labelKey, action.fallback);
        const busy = busyId === action.id;
        const disabled = busyId != null || activityLocked;
        const { Icon } = action;

        if (compact) {
          return (
            <Tooltip key={action.id} title={label}>
              <span>
                <IconButton size="small" disabled={disabled} aria-label={label} onClick={e => handleClick(e, action)}>
                  {busy ? <CircularProgress size={16} /> : <Icon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        return (
          <Button
            key={action.id}
            size="small"
            variant="contained"
            color="primary"
            disabled={disabled}
            startIcon={busy ? <CircularProgress size={14} /> : <Icon fontSize="small" />}
            onClick={e => handleClick(e, action)}
            sx={COMMAND_BUTTON_SX}
          >
            {label}
          </Button>
        );
      })
    : null;

  return (
    <>
      <Stack
        direction="row"
        spacing={compact ? 0.5 : 1}
        flexWrap={compact ? 'nowrap' : 'wrap'}
        justifyContent={compact ? 'flex-start' : 'center'}
        useFlexGap
        sx={compact ? null : { width: '100%', '& .MuiButton-root': COMMAND_BUTTON_SX }}
      >
        {buttons}
        {appendActions}
      </Stack>

      <Dialog open={!!rebootCommand} onClose={() => setRebootCommand(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('sites.reboot', 'Reboot')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('sites.rebootConfirm', 'Reboot this unit? It will briefly go offline while it restarts.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootCommand(null)}>{t('actions.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={activityLocked || busyId != null}
            onClick={confirmReboot}
          >
            {t('sites.reboot', 'Reboot')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!statusChoices} onClose={() => setStatusChoices(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('sites.chooseStatusType', 'Choose status type')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('sites.chooseStatusTypeHint', 'Choose which status request to send to this unit.')}
          </DialogContentText>
          <Stack spacing={1} sx={{ mt: 2 }}>
            {(statusChoices ?? []).map(command => (
              <Button
                key={command.key}
                variant="outlined"
                disabled={activityLocked || busyId != null}
                onClick={() => sendStatusFrame(command)}
              >
                {t(command.label_key)}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusChoices(null)}>{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

SiteQuickActions.propTypes = {
  appendActions: PropTypes.node,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canSendCommand: PropTypes.bool,
  compact: PropTypes.bool,
};
